import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import ImageUploadButton from "../../components/admin/ImageUploadButton";
import { SERVICES } from "../../data/site";

const CATEGORIES = [...SERVICES.map((s) => s.titleJa), "不動産開発"];
const EMPTY = { title: "", category: "土木工事", location: "", year: "", description: "", image_url: "", before_image_url: "", after_image_url: "", detail: "", published: true };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminWorks() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/works")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("取得に失敗しました"));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing({});
  };

  const openEdit = (w) => {
    setForm({ title: w.title, category: w.category, location: w.location, year: w.year, description: w.description, image_url: w.image_url, before_image_url: w.before_image_url || "", after_image_url: w.after_image_url || "", detail: w.detail || "", published: w.published });
    setEditing(w);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/works/${editing.id}`, form);
      else await api.post("/admin/works", form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`「${w.title}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/works/${w.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-works-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">施工実績の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">WORKS MANAGEMENT</p>
        </div>
        <button
          data-testid="works-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
        >
          <Plus className="h-4 w-4" />
          新規追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">タイトル</th>
              <th className="px-4 py-3">カテゴリ</th>
              <th className="px-4 py-3">場所</th>
              <th className="px-4 py-3">年</th>
              <th className="px-4 py-3">公開</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} data-testid={`work-row-${w.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">{w.title}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{w.category}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{w.location}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{w.year}</td>
                <td className="px-4 py-3 text-xs">{w.published ? <span className="text-green-400">公開</span> : <span className="text-gray-500">非公開</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`work-edit-${w.id}`} onClick={() => openEdit(w)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`work-delete-${w.id}`} onClick={() => remove(w)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="work-form" onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "実績を編集" : "実績を追加"}</h2>
              <button type="button" data-testid="work-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Field label="タイトル *">
              <input data-testid="work-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="カテゴリ *">
                <select data-testid="work-form-category" value={form.category} onChange={set("category")} className={inputCls}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="場所">
                <input data-testid="work-form-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="三重県津市" />
              </Field>
              <Field label="年">
                <input data-testid="work-form-year" value={form.year} onChange={set("year")} className={inputCls} placeholder="2025" />
              </Field>
            </div>
            <Field label="説明">
              <textarea data-testid="work-form-description" rows={3} value={form.description} onChange={set("description")} className={`resize-y ${inputCls}`} />
            </Field>
            <Field label="画像URL">
              <div className="flex items-start gap-2">
                <input data-testid="work-form-image" value={form.image_url} onChange={set("image_url")} className={`flex-1 ${inputCls}`} placeholder="https://..." />
                <ImageUploadButton testid="work-form-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
              </div>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="施工前 画像URL（任意）">
                <div className="flex items-start gap-2">
                  <input data-testid="work-form-before-image" value={form.before_image_url} onChange={set("before_image_url")} className={`flex-1 ${inputCls}`} placeholder="https://..." />
                  <ImageUploadButton testid="work-form-before-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, before_image_url: url }))} />
                </div>
              </Field>
              <Field label="施工後 画像URL（任意）">
                <div className="flex items-start gap-2">
                  <input data-testid="work-form-after-image" value={form.after_image_url} onChange={set("after_image_url")} className={`flex-1 ${inputCls}`} placeholder="https://..." />
                  <ImageUploadButton testid="work-form-after-image-upload" onUploaded={(url) => setForm((f) => ({ ...f, after_image_url: url }))} />
                </div>
              </Field>
            </div>
            <Field label="詳細テキスト（任意）">
              <textarea data-testid="work-form-detail" rows={4} value={form.detail} onChange={set("detail")} className={`resize-y ${inputCls}`} placeholder="詳細ページに表示する補足説明" />
            </Field>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid="work-form-published" type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-accent" />
              公開する
            </label>
            <button data-testid="work-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
